import React from "react";
import { Button, Col, Row } from "react-bootstrap";
import styled from "styled-components";
import { AnimateDiv, OrderTimeRender, SVGIcon } from "../../components/Base";
import { DB_DATE_FORMAT, DB_HOURS_FORMAT } from "../../constants";
import { formatDispatchString } from "../../helpers";
import { getMessage } from "../../helpers/branded-website";
import TrackOrderDetailsPage from "./TrackOrderDetailsPage";
import TrackMealPlanSchedulePage from "./TrackMealPlanSchedulePage";
import { isEmpty, get } from "lodash";
import moment from "moment";

function ReservationTrackCard({ data }) {
  const { order = {} } = data;
  const {
    order_details = [],
    notes = "",
    brand = {},
    payment = {},
    guest_customer = {},
  } = order;

  // Constants for Date Formatting

  const display_date_format = "MMMM D, YYYY";
  const display_time_format = "h:mm A";

  // Parse notes JSON safely
  let parsedNotes = {};
  try {
    parsedNotes = notes ? JSON.parse(notes) : {};
  } catch (e) {
    parsedNotes = {};
  }

  // --- Responsive Styles ---
  const chipStyle = {
    backgroundColor: "#7C3AED",
    color: "#fff",
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "500",
    display: "inline-block",
    maxWidth: "100%",
    wordBreak: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.4",
  };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start", // Aligns label to top if chips wrap
    padding: "16px 0",
    flexWrap: "wrap", // The "Magic" for responsiveness
    gap: "12px", // Spacing between label and chip container
    maxWidth: "100%",
  };

  const chipContainerStyle = {
    display: "flex",
    flexWrap: "wrap", // Allows chips to wrap to multiple lines
    gap: "6px", // Spacing between individual chips
    justifyContent: "flex-end",
    flex: "1 1 200px", // Allows container to grow and sets a break point at 200px
    maxWidth: "100%",
  };

  const dietary = get(parsedNotes, "dietary", []);
  const celebration = get(parsedNotes, "celebration", []);

  // Extract experience and details
  const experience = order_details.find(
    (d) => d.item && d.item.type === "reservation_experience",
  );
  const table = order_details.find(
    (d) => d.item && d.item.type === "reservation",
  );
  const experiences = order_details.filter(
    (d) => d.item && d.item.type === "reservation_experience",
  );
  const additionals = order_details.filter(
    (d) => d.item && d.item.type === "reservation_addons",
  );
  const adjustments = get(order, "reservation_notes.adjustments", {});
  const guest_count = experiences.reduce((acc, cur) => acc + (cur.qty || 0), 0);

  const amount = payment.amount || 0;

  // Icons
  const CalendarIcon = () => (
    <i
      className="fas fa-calendar"
      style={{ color: "#464E5F", marginRight: 10, width: "16px" }}
    />
  );
  const ClockIcon = () => (
    <i
      className="fas fa-clock"
      style={{ color: "#464E5F", marginRight: 10, width: "16px" }}
    />
  );
  const TicketIcon = () => (
    <i
      className="fas fa-chair"
      style={{ color: "#464E5F", marginRight: 10, width: "16px" }}
    />
  );
  const UserIcon = () => (
    <i
      className="fas fa-user"
      style={{ color: "#464E5F", marginRight: 10, width: "16px" }}
    />
  );

  const formatTime12h = (h, m) =>
    `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;

  const interval = order.reservation_notes?.dining_service?.interval ?? 0;
  const dining_length = interval;

  const [h, m] = (order.order_time || "00:00").split(":").map(Number);
  const endMin = h * 60 + m + dining_length;
  const durationHrs = Math.floor(dining_length / 60);
  const durationMins = dining_length % 60;
  const durationText = [
    durationHrs && `${durationHrs} hr${durationHrs > 1 ? "s" : ""}`,
    durationMins && `${durationMins} min${durationMins > 1 ? "s" : ""}`,
  ]
    .filter(Boolean)
    .join(" ");

  const formattedTime =
    dining_length > 0
      ? `${formatTime12h(h, m)} - ${formatTime12h(
          Math.floor(endMin / 60) % 24,
          endMin % 60,
        )} ( ${durationText} )`
      : formatTime12h(h, m);

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: "24px",
        backgroundColor: "#fff",
        color: "#1a1a1a",
        maxWidth: "600px", // Optional: keeps card readable on desktop
        margin: "0 auto",
      }}
    >
      {/* Success Header */}
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "2px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "12px",
            fontWeight: "bold",
            flexShrink: 0,
          }}
        >
          ✓
        </div>
        <h1 style={{ fontSize: "24px", fontWeight: "600", margin: 0 }}>
          Thank you for booking with us
        </h1>
      </div>

      <p
        style={{
          fontSize: "14px",
          color: "#4b5563",
          marginBottom: "32px",
          lineHeight: "1.5",
        }}
      >
        You're all set! Your reservation is confirmed. Please check your email
        for more info. We look forward to serving you soon!
      </p>

      <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "16px" }}>
        Reservation Summary
      </h2>

      {/* Main Reservation Box */}
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
          marginBottom: "12px",
        }}
      >
        <div style={{ padding: "20px" }}>
          <div
            style={{
              fontWeight: "800",
              fontSize: "18px",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            {brand.name}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#374151",
              marginBottom: "16px",
              borderBottom: "1px solid #f3f4f6",
              paddingBottom: "16px",
            }}
          >
            {experience?.item?.name} × {guest_count}
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "15px",
                textTransform: "capitalize",
              }}
            >
              <UserIcon />
              {guest_customer?.last_name} {guest_customer?.first_name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "15px",
              }}
            >
              <CalendarIcon />
              {moment(order.order_date, DB_DATE_FORMAT).format(
                display_date_format,
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "15px",
              }}
            >
              <ClockIcon />
              {formattedTime}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "15px",
              }}
            >
              <TicketIcon />
              {guest_count} seats — {table?.item?.name}
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Section (Dietary & Celebration) */}
      {(dietary.length > 0 || celebration.length > 0) && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "0 20px",
            marginBottom: "12px",
            overflow: "hidden",
          }}
        >
          {dietary.length > 0 && (
            <div
              style={{
                ...rowStyle,
                borderBottom:
                  celebration.length > 0 ? "1px solid #f3f4f6" : "none",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#4b5563",
                  fontWeight: "500",
                  flexShrink: 0,
                }}
              >
                Dietary Preference
              </span>
              <div style={chipContainerStyle}>
                {dietary.map((item, idx) => (
                  <span key={idx} style={chipStyle}>
                    {item.count ? `${item.count}× ` : ""}
                    {item.label || item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {celebration.length > 0 && (
            <div style={rowStyle}>
              <span
                style={{
                  fontSize: "14px",
                  color: "#4b5563",
                  fontWeight: "500",
                  flexShrink: 0,
                }}
              >
                Celebration
              </span>
              <div style={chipContainerStyle}>
                {celebration.map((item, idx) => (
                  <span key={idx} style={chipStyle}>
                    {item.label || item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Summary & Price Breakdown Card */}
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "20px",
          backgroundColor: "#fff",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "700",
            marginBottom: "14px",
            color: "#111827",
          }}
        >
          Payment Summary
        </div>

        {/* Experience Item(s) */}
        {experiences.map((exp, idx) => {
          const expName = exp.item?.name || exp.name || "Dining Experience";
          const expPrice = Number(exp.price ?? exp.item?.price ?? 0);
          const expQty = exp.qty || guest_count || 1;
          const expTotal = expPrice * expQty;
          return (
            <div
              key={`exp-${idx}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
                fontSize: "14px",
                gap: "12px",
              }}
            >
              <span
                style={{ color: "#4b5563", wordBreak: "break-word", flex: 1 }}
              >
                {expName} {expQty > 1 ? `× ${expQty}` : ""}
              </span>
              <span
                style={{ fontWeight: "600", color: "#1f2937", flexShrink: 0 }}
              >
                ₱
                {Number(expTotal).toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          );
        })}

        {/* Additionals Item(s) */}
        {additionals.map((add, idx) => {
          const addName = add.item?.name || add.name || "Additional Item";
          const rawPrice = add.price ?? add.item?.price ?? 0;
          const priceNum =
            typeof rawPrice === "string"
              ? Number(rawPrice.replace(/,/g, ""))
              : Number(rawPrice) || 0;
          const addQty = add.qty || 1;
          const addTotal = priceNum * addQty;
          return (
            <div
              key={`add-${idx}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
                fontSize: "14px",
                gap: "12px",
              }}
            >
              <span
                style={{ color: "#4b5563", wordBreak: "break-word", flex: 1 }}
              >
                {addName} {addQty > 1 ? `× ${addQty}` : ""}
              </span>
              <span
                style={{ fontWeight: "600", color: "#1f2937", flexShrink: 0 }}
              >
                ₱
                {Number(addTotal).toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          );
        })}

        {/* Adjustments if any */}
        {Object.values(adjustments).map((adj, idx) => {
          const adjName = adj.label || adj.name || "Adjustment";
          const adjPrice = Number(adj.price) || 0;
          return (
            <div
              key={`adj-${idx}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
                fontSize: "14px",
                gap: "12px",
              }}
            >
              <span
                style={{ color: "#4b5563", wordBreak: "break-word", flex: 1 }}
              >
                {adjName}
              </span>
              <span
                style={{ fontWeight: "600", color: "#1f2937", flexShrink: 0 }}
              >
                ₱
                {Number(adjPrice).toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          );
        })}

        <hr
          style={{
            border: "none",
            borderTop: "1px solid #f3f4f6",
            margin: "14px 0",
          }}
        />

        {/* Total Amount Paid Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "700",
            fontSize: "16px",
          }}
        >
          <span style={{ color: "#111827" }}>Total Amount Paid</span>
          <span style={{ color: "#111827", fontSize: "17px" }}>
            ₱
            {Number(amount).toLocaleString("en-PH", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Track Page - Home
 * @param {Object} props.data
 * @param {Object} props.website_theme
 * @param {boolean} props.isReservation
 * @returns
 */
export default function TrackHomepage(props) {
  const {
    data = {},
    website_theme = {},
    showOrderDetailsPage = false,
    showMealPlanSchedulePage = false,
    actions = {},
    isReservation = false,
  } = props;

  const {
    icon_color = "",
    button_color = "",
    button_text_color = "",
  } = website_theme;
  const is_order_cancelled =
    data.order.status.indexOf("cancelled") !== -1 ||
    data.order.status.includes("refund");
  const is_digital = data.order.order_type === "digital";
  const dinein_table_title =
    data.order.store.custom_dinein_title || "Dine-in at table number";
  const buttonStyle = {
    backgroundColor: button_color || "#15C49A",
    borderColor: button_color || "#15C49A",
    color: button_text_color || "#ffffff",
    width: "75%",
    fontSize: "1.125rem",
  };

  const ColoredIcon = styled.span`
    svg g [fill] {
      fill: ${icon_color};
    }
  `;

  // Reservation orders get their own dedicated UI
  if (isReservation) {
    return <ReservationTrackCard data={data} />;
  }

  return (
    <>
      <AnimateDiv>
        <div
          style={{
            display:
              showMealPlanSchedulePage || showOrderDetailsPage
                ? "none"
                : "block",
          }}
        >
          <div className="text-center">
            <div style={{ margin: "0.5rem 0" }}>
              <div className="text-uppercase font-weight-bold font-pickup-sm-1">
                Order #{data.order.transaction_number}
              </div>
              {data.order.table_number && (
                <>
                  <div className="text-uppercase font-pickup-sm-1">
                    {dinein_table_title}
                  </div>
                  <div
                    className="text-uppercase font-weight-bold display-3"
                    style={{ fontSize: "1.125rem" }}
                  >
                    {data.order.table_number}
                  </div>
                </>
              )}
              <div>
                {getMessage(data.order.status, data.order.order_type, data)}
              </div>
            </div>
            <div style={{ padding: "0.25rem 0" }}>
              {is_digital ? (
                <>
                  <div className="text-uppercase font-weight-bold font-pickup-sm-1">
                    <>{`Event Time`}</>
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 500 }}>
                    {moment(data.event.start_date).format(
                      "MMMM D, YYYY h:mm A",
                    )}
                  </span>
                </>
              ) : (
                <Row>
                  <Col>
                    <div className="text-uppercase font-weight-bold font-pickup-sm-1">
                      <>{`estimated ${formatDispatchString(
                        data.order.order_type,
                      )} time`}</>
                    </div>
                    <OrderTimeRender
                      trackPage={true}
                      noRange={!(data.order.status === "new")}
                      store={data.order.store}
                      {...data.order}
                    />
                  </Col>
                </Row>
              )}
            </div>
            <div style={{ padding: "0.25rem 0" }}>
              <Row>
                <Col>
                  <div className="text-uppercase font-weight-bold font-pickup-sm">
                    <>contact our store</>
                  </div>
                  <div style={{ fontSize: "1rem" }}>
                    <i
                      className="fas fa-phone text-success"
                      style={{ transform: "rotateY(180deg)" }}
                    />
                    &nbsp;&nbsp;
                    <span className="font-weight-bold mv-1rem">
                      <a
                        style={{
                          color: website_theme.link_text_color,
                          textDecoration: "underline",
                        }}
                        href={
                          data.order.store.contact_number
                            ? `tel:${data.order.store.contact_number}`
                            : ""
                        }
                      >
                        {data.order.store.contact_number}
                      </a>
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="font-pickup-sm-1 font-weight-normal">
              Share order details{" "}
              <Button
                variant="custom"
                className="btn-icon"
                style={{ marginLeft: "-5px" }}
                onClick={props.actions.onClickShare}
              >
                <ColoredIcon>
                  <SVGIcon icon={"Files/Share"} title="Share" />
                </ColoredIcon>{" "}
              </Button>{" "}
            </div>
            {!isEmpty(data) &&
              data.order.order_type === "delivery" &&
              ["ready", "completed"].includes(data.status) > -1 &&
              !isEmpty(data.delivery_data) &&
              data.delivery_data.status !== "COMPLETED" && (
                <div style={{ fontSize: "1.125rem" }}>
                  <ColoredIcon>
                    <SVGIcon icon={"Map/Position"} title="Position" />
                  </ColoredIcon>{" "}
                  &nbsp;&nbsp;
                  <span className="font-weight-bold">
                    <a
                      style={{
                        color: "#464E5F",
                        textDecoration: "underline",
                        marginLeft: "-10px",
                      }}
                      href={
                        data.order.delivery.delivery_partner === "lalamove"
                          ? `${data.delivery_data.shareLink}`
                          : data.order.delivery.delivery_partner === "mrspeedy"
                          ? `${
                              data.order.delivery &&
                              (data.order.delivery.lastStatus === "planned" ||
                                data.order.delivery.lastStatus ===
                                  "MULTI_ASSIGNING" ||
                                data.order.delivery.lastStatus ===
                                  "courier_assigned" ||
                                data.order.delivery.lastStatus ===
                                  "courier_departed")
                                ? `${data.delivery_data.orders[0].points[0].tracking_url}`
                                : `${data.delivery_data.orders[0].points[1].tracking_url}`
                            }`
                          : data.order.delivery.delivery_partner === "grab"
                          ? `${data.delivery_data.trackingURL}`
                          : ""
                      }
                      target="_blank"
                    >
                      Track your order here
                    </a>
                  </span>
                </div>
              )}
            <div className="d-flex flex-column justify-content-center align-items-center">
              {data.order.order_type === "curbside_pickup" &&
              !is_order_cancelled ? (
                <>
                  {!data.order.curbside_pickup_details.hasArrived ? (
                    <Button
                      style={buttonStyle}
                      className="btn-block btn-md font-weight-bold mt-4"
                      onClick={() =>
                        props.handleCustomerArrived(data.order._id)
                      }
                    >
                      I have arrived
                    </Button>
                  ) : (
                    <Button
                      style={buttonStyle}
                      className="btn-block font-weight-bold mt-4 disabled"
                    >
                      {data.order.status === "accepted"
                        ? "We are preparing your order please hang tight"
                        : "Coming up"}
                    </Button>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>

        {showOrderDetailsPage && (
          <TrackOrderDetailsPage
            data={data}
            actions={actions}
            website_theme={website_theme}
          />
        )}
        {showMealPlanSchedulePage && (
          <TrackMealPlanSchedulePage
            data={data}
            actions={actions}
            website_theme={website_theme}
          />
        )}
      </AnimateDiv>
    </>
  );
}
